import type { ScaleLinear, ScaleTime } from 'd3-scale'
import { curveMonotoneX, line } from 'd3-shape'
import { useMemo } from 'react'

import type { PriceLine } from '@/features/bidding/components/cascading/charts/types'

/** Radius of the marker circles when hovered */
const MARKER_RADIUS_HOVER = 7

/** Radius of the small dot markers */
const MARKER_RADIUS_DOT = 2.5

type PriceLinesProps = {
  lines: PriceLine[]
  datetimes: Date[]
  timeScale: ScaleTime<number, number>
  valueScale: ScaleLinear<number, number>
  highlightedLevelId?: string | null
  /** When set, only show markers for this hour index */
  hoveredHour?: number | null
}

/**
 * Renders smooth price lines with labeled markers at the center of each hour.
 */
function PriceLines({
  lines,
  datetimes,
  timeScale,
  valueScale,
  highlightedLevelId,
  hoveredHour,
}: Readonly<PriceLinesProps>) {
  // Calculate the hour interval to position markers at center of each hour
  const hourInterval = useMemo(() => {
    if (datetimes.length < 2) return 0
    return datetimes[1].getTime() - datetimes[0].getTime()
  }, [datetimes])

  const lineData = useMemo(() => {
    if (lines.length === 0 || datetimes.length === 0) return []

    return lines.map((lineInfo) => {
      // Create points at center of each hour
      const centerPoints = lineInfo.points
        .map((point) => {
          const datetime = datetimes[point.hour]
          if (!datetime) return null
          // Center of the hour = start + half interval
          const centerTime = new Date(datetime.getTime() + hourInterval / 2)
          return { datetime: centerTime, price: point.price, hour: point.hour }
        })
        .filter((p): p is NonNullable<typeof p> => p !== null)

      const lineGenerator = line<{ datetime: Date; price: number }>()
        .x((d) => timeScale(d.datetime))
        .y((d) => valueScale(d.price))
        .curve(curveMonotoneX)
        .defined((d) => Number.isFinite(d.price))

      const isHighlighted = highlightedLevelId === lineInfo.levelId
      const strokeWidth = isHighlighted ? 2.5 : 1.5

      return {
        key: `${lineInfo.levelId}-${lineInfo.direction}`,
        levelIndex: lineInfo.levelIndex,
        path: lineGenerator(centerPoints) ?? '',
        markers: centerPoints.map((p) => ({
          cx: timeScale(p.datetime),
          cy: valueScale(p.price),
          hour: p.hour,
        })),
        color: lineInfo.color,
        strokeWidth,
        isHighlighted,
      }
    })
  }, [lines, datetimes, timeScale, valueScale, highlightedLevelId, hourInterval])

  // Sort so highlighted lines render last (on top)
  const sortedData = useMemo(() => {
    const nonHighlighted = lineData.filter((d) => !d.isHighlighted)
    const highlighted = lineData.filter((d) => d.isHighlighted)
    return [...nonHighlighted, ...highlighted]
  }, [lineData])

  return (
    <g className="price-lines">
      {sortedData.map((data) => (
        <g key={data.key}>
          {/* Line path */}
          <path d={data.path} fill="none" stroke={data.color} strokeWidth={data.strokeWidth} />
          {/* Markers - small colored dots that expand to larger markers with numbers on hover */}
          {data.markers.map((marker) => {
            const isHovered = hoveredHour !== null && hoveredHour !== undefined && marker.hour === hoveredHour
            return (
              <g key={`${data.key}-${marker.hour}`}>
                <circle
                  cx={marker.cx}
                  cy={marker.cy}
                  fill={isHovered ? 'white' : data.color}
                  r={isHovered ? MARKER_RADIUS_HOVER : MARKER_RADIUS_DOT}
                  stroke={isHovered ? data.color : 'none'}
                  strokeWidth={1.5}
                />
                {isHovered && (
                  <text
                    dominantBaseline="central"
                    fill={data.color}
                    fontSize="7"
                    fontWeight="600"
                    textAnchor="middle"
                    x={marker.cx}
                    y={marker.cy}
                  >
                    {data.levelIndex + 1}
                  </text>
                )}
              </g>
            )
          })}
        </g>
      ))}
    </g>
  )
}

export default PriceLines
